import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  ArrowRight,
  FileDown,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { fetchSeoInteraction, fetchSeoInteractionByCombinedSlug, downloadDiscussionReportPdf } from "../lib/api";
import { useAuth } from "../lib/auth";
import SeoHead from "../components/SeoHead";

const RISK_VISUAL = {
  HIGH: {
    tone: "bg-rose-50 text-rose-800 border-rose-200",
    chip: "bg-rose-100 text-rose-800 border-rose-200",
    Icon: AlertCircle,
  },
  MODERATE: {
    tone: "bg-amber-50 text-amber-900 border-amber-200",
    chip: "bg-amber-100 text-amber-900 border-amber-200",
    Icon: AlertTriangle,
  },
  LOW: {
    tone: "bg-emerald-50 text-emerald-800 border-emerald-200",
    chip: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Icon: CheckCircle2,
  },
  NEUTRAL: {
    tone: "bg-cream-100 text-ink-soft border-surface-line",
    chip: "bg-cream-100 text-ink-soft border-surface-line",
    Icon: HelpCircle,
  },
};

const DISCLAIMER =
  "This information is for educational purposes only and is not a substitute for advice from your prescribing physician or dermatologist.";

function Skeleton() {
  return (
    <main className="bg-cream-50 min-h-[80vh]">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 space-y-6 animate-pulse">
        <div className="h-3 w-2/3 bg-cream-200 rounded" />
        <div className="h-10 w-full bg-cream-200 rounded" />
        <div className="h-32 w-full bg-cream-200 rounded-2xl" />
        <div className="h-4 w-3/4 bg-cream-200 rounded" />
        <div className="h-4 w-2/3 bg-cream-200 rounded" />
      </div>
    </main>
  );
}

function NotFound() {
  return (
    <main className="bg-cream-50 min-h-[80vh] flex items-center justify-center px-5">
      <div
        data-testid="seo-interaction-not-found"
        className="max-w-md text-center space-y-5"
      >
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink">
          This interaction isn't in our database yet.
        </h1>
        <p className="text-ink-soft">
          Run a personalized check on any product to see how it interacts with
          your medication.
        </p>
        <Link
          to="/check"
          data-testid="seo-interaction-not-found-cta"
          className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Check a product <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </main>
  );
}

export default function IngredientInteractionPage({ onPaywall }) {
  const params = useParams();
  // Supports two URL patterns:
  //   /ingredients/:medication/:ingredient   (canonical, segment-based)
  //   /interaction/:slug                      (shareable, {med}-{ingredient})
  // For the single-slug form, the backend resolves the correct split since
  // both medication and ingredient slugs may themselves contain hyphens
  // (e.g. "combined-oral-contraceptive-salicylic-acid").
  const medication = params.medication;
  const ingredient = params.ingredient;
  const combined = !medication && params.slug ? params.slug : null;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = !!user?.is_pro;

  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | not_found

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const res = combined
          ? await fetchSeoInteractionByCombinedSlug(combined)
          : await fetchSeoInteraction(medication, ingredient);
        if (cancelled) return;
        setData(res);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        setStatus(e?.response?.status === 404 ? "not_found" : "not_found");
      }
    })();
    return () => { cancelled = true; };
  }, [medication, ingredient, combined]);

  if (status === "loading") return <Skeleton />;
  if (status === "not_found" || !data) return <NotFound />;

  const { medication: med, ingredient: ing, interaction, related, meta } = data;
  const visual = RISK_VISUAL[interaction.risk_level] || RISK_VISUAL.NEUTRAL;
  const VerdictIcon = visual.Icon;
  const generic = med.generic_name || (med.brand_names && med.brand_names[0]) || "this medication";
  const primaryBrand = (med.brand_names && med.brand_names[0]) || generic;
  const medLabel = primaryBrand && primaryBrand !== generic ? `${generic} (${primaryBrand})` : generic;
  // Resolved med slug for internal links (works for both URL shapes).
  const medSlug = medication || med.id || "";

  const handleDownloadReport = async () => {
    if (!isPro) {
      onPaywall?.();
      return;
    }
    try {
      const blob = await downloadDiscussionReportPdf({
        medications: [{ generic_name: generic, brand_names: med.brand_names }],
        product: { name: ing.name, brand: "—" },
        verdict: interaction.risk_level === "HIGH"
          ? "AVOID"
          : interaction.risk_level === "MODERATE"
            ? "CAUTION"
            : interaction.risk_level === "LOW" ? "SAFE" : "INSUFFICIENT_DATA",
        triggered_by: [{
          ingredient_name: ing.name,
          medication_name: generic,
          risk_level: interaction.risk_level,
          reasoning: interaction.reasoning,
        }],
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "blendsafe-discussion-report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Discussion report downloaded", {
        description: "Bring the PDF to your next dermatology appointment \u2014 it's formatted for clinical review.",
      });
    } catch (e) {
      if (e?.response?.status === 402) {
        onPaywall?.();
        return;
      }
      toast.error("Couldn't generate the PDF", {
        description: "Check your connection and try again \u2014 your check is still saved.",
      });
    }
  };

  return (
    <main data-testid="seo-interaction-page" className="bg-cream-50 min-h-[80vh] overflow-x-clip">
      <SeoHead
        title={meta.title}
        description={meta.description}
        canonicalPath={meta.canonical_url}
      />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">

        {/* a) Breadcrumb */}
        <nav
          data-testid="seo-breadcrumb"
          className="flex items-center gap-1.5 text-[12.5px] text-ink-mute mb-6 flex-wrap"
        >
          <Link to="/" className="hover:text-ink transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5 text-ink-light" />
          <Link to="/browse" className="hover:text-ink transition-colors">Interactions</Link>
          <ChevronRight className="w-3.5 h-3.5 text-ink-light" />
          <Link to={`/ingredients/${medSlug}`} className="hover:text-ink transition-colors">
            {primaryBrand}
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-ink-light" />
          <span className="text-ink">{ing.name}</span>
        </nav>

        {/* b) H1 */}
        <h1
          data-testid="seo-interaction-h1"
          className="font-bold text-3xl sm:text-4xl md:text-5xl tracking-[-0.025em] text-ink leading-[1.05]"
        >
          Can you use {ing.name} with {medLabel}?
        </h1>

        {/* c) Verdict badge */}
        <section
          data-testid="seo-verdict-card"
          className={`mt-8 rounded-3xl border ${visual.tone} p-6 sm:p-7`}
        >
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/70 border border-white/80 shrink-0">
              <VerdictIcon className="w-6 h-6" strokeWidth={1.8} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold opacity-75">
                {interaction.risk_level} risk
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] mt-0.5">
                {interaction.verdict_label}
              </div>
              <p className="mt-2 text-[11.5px] leading-relaxed opacity-70">
                Information from the FDA label and published sources. Not medical advice.
              </p>
            </div>
            {interaction.verified && (
              <span
                data-testid="seo-verified-badge"
                className="hidden sm:inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] font-semibold text-emerald-800/90 bg-white/70 border border-emerald-200/70 rounded-full px-2.5 py-1 leading-none"
                aria-label={interaction.badge_text}
              >
                <ShieldCheck className="w-3 h-3" strokeWidth={2.2} />
                {interaction.badge_text}
              </span>
            )}
          </div>
          {/* d) Summary paragraph */}
          {interaction.summary && (
            <p
              data-testid="seo-summary"
              className="mt-5 text-[15px] sm:text-[16px] leading-relaxed text-ink"
            >
              {interaction.summary}
            </p>
          )}
        </section>

        {/* e/f) Reasoning */}
        {interaction.reasoning && (
          <section className="mt-10">
            <h2 className="font-bold text-xl sm:text-2xl tracking-[-0.02em] text-ink">
              Why dermatologists flag this combination
            </h2>
            <p
              data-testid="seo-reasoning"
              className="mt-4 text-[15px] leading-relaxed text-ink-soft whitespace-pre-line"
            >
              {interaction.reasoning}
            </p>
          </section>
        )}

        {/* g) Disclaimer */}
        <p
          data-testid="seo-disclaimer"
          className="mt-10 text-[12.5px] text-ink-mute italic leading-relaxed"
        >
          {DISCLAIMER}
        </p>

        {/* h) Primary CTA */}
        <section
          data-testid="seo-cta-check"
          className="mt-10 rounded-3xl bg-white border border-surface-line p-6 sm:p-8"
        >
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-ink-mute font-semibold">
            Run a personalized check
          </div>
          <h3 className="mt-2 font-bold text-xl sm:text-2xl tracking-[-0.02em] text-ink">
            Check your full skincare routine
          </h3>
          <p className="mt-2 text-[14.5px] text-ink-soft leading-relaxed max-w-xl">
            See how all your products interact with {generic} — a clear,
            source-linked verdict for every ingredient.
          </p>
          <Link
            to={`/check?med=${encodeURIComponent(medSlug)}`}
            data-testid="seo-cta-check-btn"
            className="mt-5 inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Check a product <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {/* i) Related interactions */}
        {related && related.length > 0 && (
          <section data-testid="seo-related" className="mt-12">
            <h2 className="font-bold text-xl sm:text-2xl tracking-[-0.02em] text-ink">
              Other ingredients to know about with {generic}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {related.map((r) => {
                const rv = RISK_VISUAL[r.risk_level] || RISK_VISUAL.NEUTRAL;
                return (
                  <Link
                    key={r.url}
                    to={r.url}
                    data-testid={`seo-related-${r.ingredient_id}`}
                    className="block rounded-2xl bg-white border border-surface-line p-5 hover:shadow-card transition-all hover:scale-[1.01]"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`text-[10px] uppercase tracking-[0.18em] font-semibold border rounded-full px-2 py-0.5 ${rv.chip}`}
                      >
                        {r.risk_level}
                      </span>
                      <span className="text-[11.5px] text-ink-mute">{r.verdict_label}</span>
                    </div>
                    <div className="font-bold text-[15px] text-ink">
                      {r.ingredient_name}
                    </div>
                    {r.summary && (
                      <p className="mt-1.5 text-[13px] text-ink-soft line-clamp-2 leading-snug">
                        {r.summary}
                      </p>
                    )}
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* j) Secondary CTA — Discussion Report */}
        <section
          data-testid="seo-cta-report"
          className="mt-12 rounded-3xl bg-ink text-cream-50 p-7 sm:p-9"
        >
          <h3 className="font-bold text-xl sm:text-2xl tracking-[-0.02em]">
            Want a report to bring to your dermatologist?
          </h3>
          <p className="mt-2 text-[14.5px] text-cream-50/80 leading-relaxed max-w-xl">
            A personalized PDF summary of your check, formatted for your next
            appointment.
          </p>
          <button
            type="button"
            onClick={handleDownloadReport}
            data-testid="seo-cta-report-btn"
            className="mt-5 inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-cream-50 text-ink hover:bg-cream-100 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <FileDown className="w-4 h-4" />
            Get the Discussion Report <ArrowRight className="w-4 h-4" />
          </button>
        </section>
      </div>
    </main>
  );
}
